import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';
import '@/components/mask/mask.css';
// Geparkt für Post-Beta: MeinInnerstes, LebenslisteSection, HerzensmenschenSection (Komponenten & Daten bleiben erhalten)
// PHASE 0: geparkt — import { ProfilFortschritt } from '@/components/ProfilFortschritt';

export default function ProfileV2() {
  const { user, logout, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const getAuthLabel = (provider) => {
    if (provider === 'google') return 'Google';
    if (provider === 'apple') return 'Apple';
    if (provider === 'facebook') return 'Facebook';
    return t('auth.email');
  };

  const getDisplayName = (profile) => {
    if (profile.displayPreference === 'username' && profile.username) return profile.username;
    return profile.name || t('profile.prefRealname');
  };

  const getUsernameStatus = (checking, available) => {
    if (checking) return t('profile.usernameChecking');
    if (available === true) return t('profile.usernameAvailable');
    if (available === false) return t('profile.usernameTaken');
    return '';
  };

  const getUsernameStatusColor = (checking, available) => {
    if (checking) return 'text-[var(--muted)]';
    if (available === true) return 'text-green-700';
    if (available === false) return 'text-red-600';
    return 'text-[var(--muted)]';
  };
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);

  // Anker-Sprung aus ICH (z.B. /profile-classic#public-profile-section)
  useEffect(() => {
    if (!location.hash || loading) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [location.hash, loading]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editDisplayPref, setEditDisplayPref] = useState('realname');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [digestFrequency, setDigestFrequency] = useState('weekly');
  const [digestSaving, setDigestSaving] = useState(false);
  const [editInstagram, setEditInstagram] = useState('');
  const [publicEnabled, setPublicEnabled] = useState(false);
  const [publicUsername, setPublicUsername] = useState('');
  const [publicToggling, setPublicToggling] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    let cancelled = false;
    async function loadProfile() {
      setLoading(true); setError(null);
      try {
        const res = await axios.get(`${API_V2}/profile/me`, { withCredentials: true });
        if (!cancelled) {
          setStats(res.data);
          setEditName(res.data.name || ''); setEditEmail(res.data.email || '');
          setEditPhone(res.data.phone || ''); setEditUsername(res.data.username || '');
          setEditDisplayPref(res.data.displayPreference || 'realname');
          setDigestFrequency(res.data.digestFrequency || 'weekly');
          setEditInstagram(res.data.instagramHandle || '');
          setPublicEnabled(!!res.data.publicProfileEnabled);
          setPublicUsername(res.data.username || '');
        }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
          setError(err.response?.data?.detail || t('profile.loadError'));
        }
      }
      finally { if (!cancelled) setLoading(false); }
    }
    loadProfile();
    return () => { cancelled = true; };
  }, [user, API_V2, navigate, t]);

  const startEditing = () => { setEditing(true); setSaveError(null); setSaveSuccess(false); };
  const cancelEditing = () => { setEditing(false); setEditName(stats?.name || ''); setEditEmail(stats?.email || ''); setEditPhone(stats?.phone || ''); setEditUsername(stats?.username || ''); setEditDisplayPref(stats?.displayPreference || 'realname'); setUsernameAvailable(null); setSaveError(null); };

  const saveProfile = async () => {
    setSaving(true); setSaveError(null); setSaveSuccess(false);
    try {
      const res = await axios.put(`${API_V2}/profile/update`, { name: editName.trim() || null, email: editEmail.trim() || null, phone: editPhone.trim() || null, username: editUsername.trim() || null, display_preference: editDisplayPref, instagramHandle: editInstagram.trim() || null }, { withCredentials: true });
      setStats(prev => ({ ...prev, name: res.data.profile?.name, email: res.data.profile?.email, phone: res.data.profile?.phone, username: res.data.profile?.username, displayPreference: res.data.profile?.display_preference, instagramHandle: res.data.profile?.instagram_handle }));
      setEditing(false); setSaveSuccess(true); setUsernameAvailable(null);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) { setSaveError(err.response?.data?.detail || t('profile.saveError')); }
    finally { setSaving(false); }
  };

  const checkUsername = async (val) => {
    setEditUsername(val); setUsernameAvailable(null);
    const clean = val.trim();
    if (!clean || clean.length < 3) return;
    setCheckingUsername(true);
    try { const res = await axios.get(`${API_V2}/username/check?username=${encodeURIComponent(clean)}`, { withCredentials: true }); setUsernameAvailable(res.data.available); }
    catch (_e) { setUsernameAvailable(null); }
    finally { setCheckingUsername(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault(); setPasswordError(null); setPasswordSuccess(false);
    if (newPassword !== confirmPassword) { setPasswordError(t('profile.passwordMismatch')); return; }
    if (newPassword.length < 6) { setPasswordError(t('profile.passwordTooShort')); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API_V2}/profile/password`, { currentPassword, newPassword }, { withCredentials: true });
      setPasswordSuccess(true); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setShowPasswordForm(false);
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) { setPasswordError(err.response?.data?.detail || t('common.error')); }
    finally { setChangingPassword(false); }
  };

  const updateDigest = async (freq) => {
    setDigestSaving(true);
    try {
      await axios.put(`${API_V2}/profile/digest`, { digestFrequency: freq }, { withCredentials: true });
      setDigestFrequency(freq);
    } catch (err) { console.error('Digest update failed:', err); }
    finally { setDigestSaving(false); }
  };

  const togglePublicProfile = async () => {
    setPublicToggling(true);
    try {
      const res = await axios.post(`${API_V2}/profile/public-toggle`, { enabled: !publicEnabled }, { withCredentials: true });
      setPublicEnabled(res.data.enabled);
      window.dispatchEvent(new Event('cc-progress-refresh'));
      if (res.data.username) {
        setPublicUsername(res.data.username);
        setStats(prev => (prev ? { ...prev, username: res.data.username } : prev));
        setEditUsername(res.data.username);
      }
    } catch (err) { console.error('Public profile toggle failed:', err); }
    finally { setPublicToggling(false); }
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/@${publicUsername}`);
      setPublicLinkCopied(true);
      setTimeout(() => setPublicLinkCopied(false), 2000);
    } catch (_e) { /* clipboard unavailable */ }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/v2/og/profile/${publicUsername}`);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    } catch (_e) { /* clipboard unavailable */ }
  };

  if (!user) return null;
  const firstName = (user.name || '').split(' ')[0];

  const sectionLabel = 'block text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-6';
  const fieldLabel = 'block text-xs uppercase tracking-[0.14em] text-[var(--muted)] mb-1.5';
  const maskField = 'mask-input py-2 text-[15px] text-[var(--text)]';
  const pillPrimary = 'mask-primary rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-6 py-2.5 text-sm disabled:opacity-60';
  const quietLink = 'text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors';
  const togglePill = (active) => `rounded-full border px-4 py-1.5 text-sm transition-colors disabled:opacity-50 ${active ? 'border-[var(--text)] text-[var(--text)]' : 'border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)]'}`;

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden" data-testid="konto-room">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true">
        <span style={{ transform: 'translateX(-4%)' }}>{t('v2.bgWords.konto')}</span>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-16 pb-28">
        <button
          type="button"
          onClick={() => navigate('/profile')}
          data-testid="konto-back-link"
          className="mask-enter inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--text)] transition-colors mb-14"
        >
          <span aria-hidden="true">←</span> {t('v2.konto.back')}
        </button>

        <header className="mb-20">
          {firstName && <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">{firstName}</p>}
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)]" data-testid="konto-headline">
            {t('v2.konto.headline')}
          </h1>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mt-5 max-w-md">
            {t('v2.konto.sub')}
          </p>
        </header>

        {loading && <p className="text-sm text-[var(--muted)]">{t('common.loading')}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {saveSuccess && <p className="text-sm text-[var(--text)] mb-10" data-testid="save-success-msg">{t('profile.saved')}</p>}
        {passwordSuccess && <p className="text-sm text-[var(--text)] mb-10" data-testid="password-success-msg">{t('profile.passwordChanged')}</p>}

        {/* PHASE 0: Profil-Fortschritt (Gamification) geparkt — Komponente & API bleiben erhalten */}
        {/* Beta-Relaunch: Mein Innerstes, Lebensliste & Herzensmenschen geparkt (Daten bleiben erhalten, Reaktivierung post-Beta) */}

        {stats && (
          <div className="mask-enter mask-enter-delay-2 space-y-24">

            {/* DEINE ANGABEN */}
            <section data-testid="konto-angaben-section">
              <div className="flex items-baseline justify-between mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">{t('v2.konto.yourDetails')}</p>
                {!editing && (
                  <button type="button" onClick={startEditing} data-testid="edit-profile-btn" className={quietLink}>
                    {t('profile.edit')}
                  </button>
                )}
              </div>

              {!editing ? (
                <div className="divide-y divide-[var(--border)]/30">
                  {[
                    [t('profile.name'), stats.name || t('profile.notSet')],
                    [t('profile.username'), stats.username || t('profile.usernameNotSet')],
                    [t('profile.displayAs'), getDisplayName(stats)],
                    [t('profile.email'), stats.email || t('profile.notSet')],
                    [t('profile.phone'), stats.phone || t('profile.notSet')],
                    [t('profile.instagramLabel'), stats.instagramHandle ? `@${stats.instagramHandle}` : t('profile.notSet')],
                    [t('profile.authMethod'), getAuthLabel(stats.authProvider)],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-baseline justify-between gap-6 py-3.5">
                      <span className="text-sm text-[var(--muted)] shrink-0">{label}</span>
                      <span className="text-[15px] text-[var(--text)] text-right truncate">{value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-8">
                  <div>
                    <label className={fieldLabel}>{t('profile.editNameLabel')}</label>
                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder={t('profile.name')} data-testid="edit-name-input" className={maskField} />
                  </div>
                  <div>
                    <label className={fieldLabel}>{t('profile.editUsernameLabel')}</label>
                    <input type="text" value={editUsername} onChange={(e) => checkUsername(e.target.value)} placeholder={t('profile.editUsernamePlaceholder')} data-testid="edit-username-input" className={maskField} />
                    {editUsername.trim().length >= 3 && (
                      <p className={`text-xs mt-1.5 ${getUsernameStatusColor(checkingUsername, usernameAvailable)}`}>
                        {getUsernameStatus(checkingUsername, usernameAvailable)}
                      </p>
                    )}
                    <p className="text-xs text-[var(--muted)] mt-1.5">{t('profile.usernameHint')}</p>
                  </div>
                  <div>
                    <label className={fieldLabel}>{t('profile.displayPrefLabel')}</label>
                    <div className="flex gap-3 mt-2">
                      <button type="button" onClick={() => setEditDisplayPref('realname')} data-testid="pref-realname-btn" className={togglePill(editDisplayPref === 'realname')}>{t('profile.prefRealname')}</button>
                      <button type="button" onClick={() => setEditDisplayPref('username')} disabled={!editUsername.trim()} data-testid="pref-username-btn" className={togglePill(editDisplayPref === 'username')}>{t('profile.prefUsername')}</button>
                    </div>
                    <p className="text-xs text-[var(--muted)] mt-2">{t('profile.displayPreview', { name: editDisplayPref === 'username' && editUsername.trim() ? editUsername.trim() : editName.trim() || t('profile.name') })}</p>
                  </div>
                  <div>
                    <label className={fieldLabel}>{t('profile.email')}</label>
                    <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="deine@email.de" data-testid="edit-email-input" className={maskField} />
                  </div>
                  <div>
                    <label className={fieldLabel}>{t('profile.phone')}</label>
                    <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="+49 123 456789" data-testid="edit-phone-input" className={maskField} />
                  </div>
                  <div>
                    <label className={fieldLabel}>{t('profile.instagramLabel')}</label>
                    <input type="text" value={editInstagram} onChange={(e) => setEditInstagram(e.target.value)} placeholder={t('profile.instagramPlaceholder')} data-testid="edit-instagram-input" className={maskField} />
                    <p className="text-xs text-[var(--muted)] mt-1.5">{t('profile.instagramHint')}</p>
                  </div>
                  {saveError && <p className="text-sm text-red-600">{saveError}</p>}
                  <div className="flex items-center gap-6 pt-2">
                    <button type="button" onClick={saveProfile} disabled={saving} data-testid="save-profile-btn" className={pillPrimary}>{saving ? t('profile.saving') : t('profile.save')}</button>
                    <button type="button" onClick={cancelEditing} data-testid="cancel-edit-btn" className={quietLink}>{t('profile.cancel')}</button>
                  </div>
                </div>
              )}
            </section>

            {/* DEIN CARMA */}
            <section data-testid="konto-carma-section">
              <p className={sectionLabel}>{t('v2.konto.yourCarma')}</p>
              <div className="divide-y divide-[var(--border)]/30">
                <div className="flex items-baseline justify-between gap-6 py-3.5">
                  <span className="text-sm text-[var(--muted)]">{t('profile.receivedCarma')}</span>
                  <span className="mask-headline text-xl text-[var(--text)]">{stats.receivedCount ?? 0}</span>
                </div>
                <div className="flex items-baseline justify-between gap-6 py-3.5">
                  <span className="text-sm text-[var(--muted)]">{t('profile.sentCarma')}</span>
                  <span className="mask-headline text-xl text-[var(--text)]">{stats.sentCount ?? 0}</span>
                </div>
                <div className="flex items-baseline justify-between gap-6 py-3.5">
                  <span className="text-sm text-[var(--muted)]">{t('profile.flowCarma')}</span>
                  <span className="mask-headline text-xl text-[var(--text)]" data-testid="profile-flow-carma">{stats.flowCarmaReceived ?? 0}</span>
                </div>
              </div>
            </section>

            {/* SICHERHEIT */}
            {stats.authProvider === 'email' && (
              <section data-testid="konto-sicherheit-section">
                <p className={sectionLabel}>{t('v2.konto.security')}</p>
                {!showPasswordForm ? (
                  <button type="button" onClick={() => setShowPasswordForm(true)} data-testid="show-password-form-btn" className={quietLink}>
                    {t('profile.changePassword')}
                  </button>
                ) : (
                  <form onSubmit={changePassword} className="space-y-8">
                    <div>
                      <label className={fieldLabel}>{t('profile.currentPassword')}</label>
                      <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="current-password-input" className={maskField} />
                    </div>
                    <div>
                      <label className={fieldLabel}>{t('profile.newPassword')}</label>
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="new-password-input" className={maskField} />
                    </div>
                    <div>
                      <label className={fieldLabel}>{t('profile.confirmNewPassword')}</label>
                      <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="confirm-password-input" className={maskField} />
                    </div>
                    {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
                    <div className="flex items-center gap-6">
                      <button type="submit" disabled={changingPassword} data-testid="change-password-btn" className={pillPrimary}>{changingPassword ? t('profile.changingBtn') : t('profile.changeBtn')}</button>
                      <button type="button" onClick={() => { setShowPasswordForm(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordError(null); }} className={quietLink}>{t('profile.cancel')}</button>
                    </div>
                  </form>
                )}
              </section>
            )}

            {/* E-MAIL-ZUSAMMENFASSUNG */}
            <section data-testid="digest-settings">
              <p className={sectionLabel}>{t('digest.title')}</p>
              <p className="text-sm text-[var(--muted)] mb-5 max-w-md">{t('digest.description')}</p>
              <div className="flex gap-3">
                {['off', 'daily', 'weekly'].map((freq) => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => updateDigest(freq)}
                    disabled={digestSaving}
                    data-testid={`digest-${freq}-btn`}
                    className={togglePill(digestFrequency === freq)}
                  >
                    {t(`digest.${freq}`)}
                  </button>
                ))}
              </div>
            </section>

            {/* ÖFFENTLICHES PROFIL */}
            <section id="public-profile-section" data-testid="public-profile-settings">
              <p className={sectionLabel}>{t('publicProfile.sectionTitle')}</p>
              <p className="text-sm text-[var(--muted)] mb-6 max-w-md">{t('publicProfile.sectionDesc')}</p>
              {publicEnabled ? (
                <div className="space-y-8">
                  <p className="text-sm text-[var(--text)]">{t('publicProfile.enabled')}</p>
                  <div>
                    <p className={fieldLabel}>{t('publicProfile.yourLink')}</p>
                    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--border)]/40 pb-2">
                      <span className="text-[15px] text-[var(--text)] truncate" data-testid="public-profile-link">
                        {window.location.origin}/@{publicUsername}
                      </span>
                      <button type="button" onClick={copyPublicLink} data-testid="public-profile-copy-btn" className={`${quietLink} shrink-0`}>
                        {publicLinkCopied ? t('publicProfile.copied') : t('publicProfile.copy')}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="text-sm text-[var(--muted)] max-w-sm">{t('publicProfile.shareLinkHint')}</p>
                    <button type="button" onClick={copyShareLink} data-testid="public-profile-share-copy-btn" className={`${quietLink} shrink-0`}>
                      {shareLinkCopied ? t('publicProfile.copied') : t('publicProfile.copy')}
                    </button>
                  </div>
                  <div className="flex items-center gap-6 pt-2">
                    <button type="button" onClick={() => navigate(`/@${publicUsername}`)} data-testid="public-profile-view-btn" className={quietLink}>
                      {t('publicProfile.view')}
                    </button>
                    <button type="button" onClick={togglePublicProfile} disabled={publicToggling} data-testid="public-profile-disable-btn" className={quietLink}>
                      {t('publicProfile.disable')}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={togglePublicProfile} disabled={publicToggling} data-testid="public-profile-enable-btn" className={pillPrimary}>
                  {t('publicProfile.enable')}
                </button>
              )}
            </section>

            {/* ABMELDEN */}
            <footer className="pt-4">
              <button type="button" onClick={logout} data-testid="logout-btn" className={quietLink}>
                {t('nav.logout')}
              </button>
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}
